/** Hand-rolled OTLP/HTTP JSON export; keeps bundle small vs @opentelemetry/sdk. */

export type OtlpExportConfig = {
  endpoint: string;
  serviceName: string;
  headers?: Record<string, string>;
};

function otlpUrl(endpoint: string, path: 'v1/logs' | 'v1/traces'): string {
  return `${endpoint.replace(/\/$/, '')}/${path}`;
}

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function nowUnixNano(): string {
  return `${BigInt(Date.now()) * 1_000_000n}`;
}

function strAttr(key: string, value: string) {
  return { key, value: { stringValue: value } };
}

function resourceAttributes(serviceName: string) {
  return { attributes: [strAttr('service.name', serviceName)] };
}

/** Fire-and-forget OTLP JSON POST (keepalive for unload). */
export function otlpPost(config: OtlpExportConfig, path: 'v1/logs' | 'v1/traces', body: unknown): void {
  const url = otlpUrl(config.endpoint, path);
  const headers = { 'Content-Type': 'application/json', ...config.headers };
  const payload = JSON.stringify(body);
  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return;
    }
    void fetch(url, { method: 'POST', headers, body: payload, keepalive: true }).catch(() => undefined);
  } catch {
    /* CSP / offline */
  }
}

export function exportOtlpLog(
  config: OtlpExportConfig,
  severity: 'DEBUG' | 'INFO' | 'ERROR',
  bodyJson: string,
  attrs: Record<string, string>,
): void {
  otlpPost(config, 'v1/logs', {
    resourceLogs: [
      {
        resource: resourceAttributes(config.serviceName),
        scopeLogs: [
          {
            scope: { name: 'esan-paquetes' },
            logRecords: [
              {
                timeUnixNano: nowUnixNano(),
                severityText: severity,
                body: { stringValue: bodyJson },
                attributes: Object.entries(attrs).map(([k, v]) => strAttr(k, v)),
              },
            ],
          },
        ],
      },
    ],
  });
}

export function exportOtlpSpan(
  config: OtlpExportConfig,
  span: {
    traceId: string;
    spanId: string;
    name: string;
    startUnixNano: string;
    endUnixNano: string;
    ok: boolean;
    correlationId?: string;
  },
): void {
  const attrs = span.correlationId ? [strAttr('correlationId', span.correlationId)] : [];
  otlpPost(config, 'v1/traces', {
    resourceSpans: [
      {
        resource: resourceAttributes(config.serviceName),
        scopeSpans: [
          {
            scope: { name: 'esan-paquetes' },
            spans: [
              {
                traceId: span.traceId,
                spanId: span.spanId,
                name: span.name,
                kind: 1,
                startTimeUnixNano: span.startUnixNano,
                endTimeUnixNano: span.endUnixNano,
                attributes: attrs,
                status: { code: span.ok ? 1 : 2 },
              },
            ],
          },
        ],
      },
    ],
  });
}

export function newTraceIds(): { traceId: string; spanId: string; startUnixNano: string } {
  return { traceId: randomHex(16), spanId: randomHex(8), startUnixNano: nowUnixNano() };
}

/** Parse `OTEL_EXPORTER_OTLP_HEADERS` JSON object from build env. */
export function parseOtlpHeaders(raw: string | undefined): Record<string, string> | undefined {
  const t = raw?.trim();
  if (!t) return undefined;
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(o)) {
      if (typeof v === 'string' && v) out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  } catch {
    return undefined;
  }
}
